import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, ThemeProvider, Typography,
} from '@material-ui/core';

import { usePostHog } from 'posthog-js/react';
import { useLazyQuery } from '@apollo/client';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';

import sleepioTheme from '@/themes/sleepioTheme';
import EmailAuthForm from '@/pages/Onboarding/components/EmailAuthForm/EmailAuthForm';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { activateEmail } from '@/utils/sparkActivationApi';
import { getLob } from '@/utils/productHelpers';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { SLEEPIO_EMAIL_INTENT_SESSION_KEY } from '@/utils/constants';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { logError } from '@/utils/logError';

const EmailEntryInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const base = useActivationBase();
  const { state, dispatch } = useActivation();
  const isCreate = state.accountIntent === AccountIntent.Create;
  const rawRedirectTo = new URLSearchParams(search).get('redirectTo');
  const redirectTo = rawRedirectTo && isSafeRedirectUri(rawRedirectTo) ? rawRedirectTo : undefined;

  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch({ type: 'SET_STEP', step: 'email_entry' });
    dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email' });
  }, [dispatch]);

  const handleSubmit = async (email: string) => {
    setLoading(true);
    // Store intent before the tab hop so EmailAuthCallback can route correctly
    // without relying on JWT entitlement status (unreliable for email — no accessCode signal).
    sessionStorage.setItem(SLEEPIO_EMAIL_INTENT_SESSION_KEY, isCreate ? 'sign-up' : 'login');
    try {
      await activateEmail({
        email,
        product: 'sleepio',
        organizationSlug: state.orgSlug,
        lob: getLob(state.orgSlug),
        redirectTo,
        phDistinctId: posthog?.get_distinct_id() || undefined,
      });

      try {
        const result = await getDistinctId({ variables: { email } });
        const distinctId = result?.data?.posthogDistinctIdWithEmail?.distinctId;
        if (distinctId) {
          posthog?.identify(distinctId, { email });
        }
      } catch (err) {
        logError('[EmailEntry]', 'PostHog identify failed', err);
      }

      dispatch({ type: 'SET_EMAIL', email });
      dispatch({ type: 'SET_STEP', step: 'magic_link_sent' });
      history.push(`${base}/sent`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 40 }}>
          <Typography variant="h2" color="textPrimary" style={{ marginBottom: 8 }}>
            {t('coverage.pre_onboarding.sleepio.activation.email_entry.title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.email_entry.sub_title')}
          </Typography>
        </Box>
        <EmailAuthForm
          onSubmit={handleSubmit}
          loading={loading}
          showConsent={isCreate}
          i18nKeys={{
            continueButton: 'coverage.pre_onboarding.sleepio.activation.email_entry.continue',
            submittingButton: 'coverage.pre_onboarding.sleepio.activation.sending',
            errorMessage: 'coverage.pre_onboarding.sleepio.activation.error_send_failed',
          }}
        />
        <ConsentWidget />
      </Container>
      <Footer />
    </Box>
  );
};

export const EmailEntry: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailEntryInner />
  </ThemeProvider>
);
